/**
 * Folder API — typed wrappers around legacy folderApi.js.
 *
 * These call the legacy API functions (which read store state for auth headers)
 * and transform the DynamoDB response shapes into clean domain types.
 */
import {
  getFolders as legacyGetFolders,
  getFolderById as legacyGetFolderById,
} from '../apiServices/folderApi';
import { addProject as legacyAddProject } from '../apiServices/projectApi';
import { transformFolder, transformFolderContent } from '../app/features/workspace/utils';

// The transform functions return plain objects — type them loosely for now.
// These will be replaced with proper typed transforms when the workspace utils are migrated.
export type FolderItem = ReturnType<typeof transformFolder>;
export type FolderContentItem = ReturnType<typeof transformFolder>;

interface FolderResponse {
  data: FolderContentItem[];
  folder?: FolderItem;
}

/**
 * Get root folder contents (all top-level items in workspace).
 */
export async function getRootFolder(): Promise<FolderResponse> {
  const response = await legacyGetFolders();
  const items = transformFolderContent(response.response.Items);
  const folder = response.response.Item
    ? transformFolder(response.response.Item)
    : undefined;
  return { data: items, folder };
}

/**
 * Get a specific folder's contents by ID.
 */
export async function getFolderById(id: string): Promise<FolderResponse> {
  const response = await legacyGetFolderById(id);
  const items = transformFolderContent(response.response.Items);
  const folder = transformFolder(response.response.Item);
  return { data: items, folder };
}

/**
 * Create a new folder inside a parent folder (or root).
 * The POST response may not include the full Item, so we just confirm success.
 */
export async function createFolder(title: string, parentId: string): Promise<void> {
  await legacyAddProject({ title, type: 'folder', parent: parentId });
}
